import { get, sortBy, isEmpty, map, compact } from 'lodash-es';
import { TrackingConstant } from 'client/tracking/constant';
import { BRANDS } from 'site-modules/multi-offer/constants/brands';
import { MULTI_OFFER_RESULTS, OFFER_DETAILS } from 'site-modules/multi-offer/constants/render-types';
import {
  isEstimatedOffer,
  getBrandOffer,
  parseStoreRawData,
  isAppraisalsOfferSource as getIsAppraisalsOfferSource,
} from 'site-modules/multi-offer/utils/offers';
import { isExpiredOffer } from 'site-modules/shared/utils/multi-offer/offers';
import { PARTNERS } from 'site-modules/shared/constants/multi-offer/partners';

import { withMetrics } from 'client/data/api/api-metrics';
import { EdmundsMultiOfferGatewayAPI } from 'client/data/api/api-client';
import { logger } from 'client/utils/logger/logger';

const REDACTED_VALUATION = null;

export const getOffersFromPartners = partners =>
  Object.values(partners || {}).flatMap(partner => get(partner, 'offers', []));

export const getOffersFromModsRecord = modsRecord => getOffersFromPartners(modsRecord?.partners);

export const getSolicitedPartners = partnersToSolicit => {
  if (!Array.isArray(partnersToSolicit) || !partnersToSolicit.length) {
    return [];
  }
  return (
    partnersToSolicit
      // This function is expected to omit CarMax.
      .filter(partner => partner !== PARTNERS.CARMAX)
      // Currently, all partners have the same 'partnerToSolicit' and 'partnerKey'.
      .map(partner => ({ partnerToSolicit: partner, partnerKey: partner }))
  );
};

/**
 * @param {Object} partnerData
 * @param {Array} solicitedPartners
 * @returns {Object} subcopy of partnerData with only solicited partners key/values
 */
export const filterPartnerData = (partnerData, solicitedPartners) => {
  if (isEmpty(solicitedPartners)) {
    return {};
  }
  const filteredPartners = Object.keys(partnerData).filter(partnerLabel =>
    solicitedPartners.some(partner => partner.partnerKey === partnerLabel)
  );
  return filteredPartners.reduce(
    (result, partnerLabel) => ({
      ...result,
      [partnerLabel]: partnerData[partnerLabel],
    }),
    {}
  );
};

export const sortQuestionsByDisplayOrder = questions => sortBy(questions, ['displayOrder']);

/**
 * Fetches and processes multi-offer condition questions from the API.
 *
 * @param {Object} options - Configuration options
 * @param {string} options.endpoint - API endpoint to fetch questions from
 * @param {string} options.errorContext - Context message for error logging
 * @param {Object} [options.context] - Optional context for withMetrics
 * @param {Boolean} [options.shouldSort=true] - Optional flag that indicates if the questions should be sorted by display order. Defaults to true.
 * @returns {Promise<Object>} Object containing questions array and potential error info
 */
export const fetchAndProcessQuestions = async ({ endpoint, errorContext, context, shouldSort = true }) => {
  const result = {};
  let questions = [];

  try {
    const api = context ? withMetrics(EdmundsMultiOfferGatewayAPI, context) : EdmundsMultiOfferGatewayAPI;
    questions = await api.fetchJson(endpoint);
  } catch (e) {
    logger.warn(e, errorContext);
    result.isError = true;
    result.message = e.message;
    result.status = e.status;
  }

  let resultQuestions = questions;
  if (shouldSort) {
    resultQuestions = sortQuestionsByDisplayOrder(questions).map(({ answers, ...question }) => ({
      ...question,
      answers: answers.map(answer => {
        const { detailQuestions } = answer;
        return {
          ...answer,
          detailQuestions: detailQuestions ? sortQuestionsByDisplayOrder(detailQuestions) : detailQuestions,
        };
      }),
    }));
  }

  return {
    ...result,
    questions: resultQuestions,
  };
};

const isRedactedOffer = ({ valuation, redactedAt }) => valuation === REDACTED_VALUATION && redactedAt;

export const sortOffersByValuation = (o1, o2) => {
  const valuation1 = get(o1, 'valuation');
  const valuation2 = get(o2, 'valuation');

  // Declined offers (with valuation 0) are not redacted, so place them below the redacted offers.
  if (isRedactedOffer(o1) && valuation2 === 0) {
    return -1;
  }
  if (valuation1 === 0 && isRedactedOffer(o2)) {
    return 1;
  }

  return valuation2 - valuation1;
};

export const sortOffersByValuationAndExpiredStatus = (o1, o2) => {
  const o1IsExpired = isExpiredOffer(o1);
  const o2IsExpired = isExpiredOffer(o2);
  const o1Valuation = get(o1, 'valuation');
  const o2Valuation = get(o2, 'valuation');
  switch (true) {
    case o1IsExpired && !o2IsExpired:
      return 1;
    case !o1IsExpired && o2IsExpired:
      return -1;
    case !o1IsExpired && !o2IsExpired: {
      if (o1Valuation === o2Valuation) {
        const partners = [o1?.partner, o2?.partner];
        // KMX always comes before ICO if both are present
        if (partners.includes(PARTNERS.CARMAX) && partners.includes(PARTNERS.ICO)) {
          return o1.partner === PARTNERS.CARMAX ? -1 : 1;
        }
      }
      return o2Valuation - o1Valuation;
    }
    default:
      return 0;
  }
};

/**
 * Filters offers down to those that have been accepted and (valuation above 0 OR valuation is redacted)
 * @param {Object} offerDetails offer details
 * @returns {Boolean} whether the offer is valid to be displayed to the user
 */
export const acceptedOfferFilter = offerDetails => {
  const { status, valuation } = offerDetails;
  return status === 'accepted' && (isRedactedOffer(offerDetails) || valuation > 0);
};

/**
 * Filters offers down to those that have been declined
 * @param {Object} offer offer details
 * @param {String} offer.status accepted or declined status of offer
 * @returns {Boolean} whether the offer is declined
 */
export const declinedOfferFilter = ({ status }) => status === 'declined';

/**
 * Returns number of expired offers
 * @param {Array} offers array of offer objects
 * @returns {Number} count of expired offers
 */
export const getNumberOfExpiredOffers = offers => offers.reduce((a, b) => a + (isExpiredOffer(b) ? 1 : 0), 0);

/**
 * This constructs the offer object to include the brand and source.
 * Brand and source will fallback to the "partner" key if it does not exist.
 * @param {Object} offer offer details
 * @param {String} partner - Partner Key
 * @param {Object} partnerObject the object that contains all the partner related fields; currently a MODS record has
 * a top-level "partners" object, it contains a "partner object" for each partner.
 * @returns {Object}
 */
export const constructOfferObject = (offer, partner, partnerObject) => {
  const brand = get(offer, 'brand', partner);
  const brandAssets = offer?.brandAssets ?? partnerObject?.brandAssets;
  const result = {
    ...offer,
    partner,
    brand,
    source: get(offer, 'source', partner),
    redemptionInfoResponse: partnerObject?.redemptionInfoResponse,
    brandAssets,
  };
  if (result.store?.rawData) {
    result.store = {
      ...result.store,
      // Fill in store.data with parsed data.
      data: parseStoreRawData(result.store.rawData, true, brand),
      // Replace store.rawData with explanatory string.
      rawData: 'Raw data is removed. Use store.data with parsed data.',
    };
  }
  if (result.brand === BRANDS.ICO) {
    // Fill in 'dealerName' field, because it's used in UI in many places.
    result.dealerName = result.store?.data?.name;
  }
  return result;
};

/**
 * Given the solicitedResponse value, it determines whether it is in a "okay/can proceed" state (either successful status or "undefined").
 *
 * Important:
 *  - "solicitedResponse === undefined" is REQUIRED.
 *    - In this case, "undefined >= 200 && undefined < 300" returns "false", but we need "undefined" to pass this check for our business logic.
 *    - Between the solicit request being made on the VAC and the user being redirected to the MOR page, there are a few seconds when we won't
 *      have a response from our partner yet and "solicitedResponse === undefined", so we have to account for that.
 *    - See https://gitlab.com/edmundscom/edmunds/node-site-venom/-/merge_requests/18931#note_773720 for full details.
 * @param {Number|undefined} solicitedResponse
 * @returns {Boolean}
 */
export const isSolicitedResponseOkay = solicitedResponse =>
  (solicitedResponse >= 200 && solicitedResponse < 300) || solicitedResponse === undefined;

/**
 * Returns whether the page is in the "offers pending" state (i.e. partner offers are not finalized yet).
 *
 * @param {Object} partnerData partner data, includes:
 *    @param {Array} offers partner offers.
 *    @param {Number|null} solicitedResponse the response code from the solicitation request to the partner.
 * @returns {Boolean} whether the page is in the "offers pending" state.
 */
export const areOffersPending = ({ offers, solicitedResponse }) =>
  offers === undefined && isSolicitedResponseOkay(solicitedResponse);

/**
 * Returns whether the KMX offer has been finalized for the given MODS record.
 *
 * @param {Object} modsRecord MODS record containing partners.kmx partner data.
 * @returns {Boolean} true if the KMX offer is finalized, false if still pending.
 */
export const isKmxOfferFinalized = modsRecord => {
  const kmxPartnerObject = modsRecord?.partners?.kmx ?? {};
  const { offers, solicitedResponse } = kmxPartnerObject;
  return !(offers === undefined && isSolicitedResponseOkay(solicitedResponse));
};

/**
 * Returns whether every partner offer has been finalized for the given MODS record.
 *
 * @param {Object} modsRecord MODS record.
 * @returns {Boolean} whether partnersOffersFinalizedAt is present.
 */
export const areAllOffersFinalized = (modsRecord = {}) => {
  const { partnersOffersFinalizedAt } = modsRecord;
  return Boolean(partnersOffersFinalizedAt);
};

export const generateSubactionPageloadTracking = (subActionName, value) => ({
  event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
  event_data: {
    action_name: TrackingConstant.ACTION_RECEIVE_EPO,
    action_category: TrackingConstant.SYSTEM_ACTION_CATEGORY,
    action_cause: TrackingConstant.PAGE_LOAD_CAUSE,
    subaction_name: subActionName,
    value,
  },
});

const getSolicitedOffers = modsRecord => {
  const partnerData = get(modsRecord, 'partners', {});
  const solicitedPartners = getSolicitedPartners(get(modsRecord, 'partnersToSolicit'));
  const filteredPartnerData = filterPartnerData(partnerData, solicitedPartners);
  return getOffersFromPartners(filteredPartnerData);
};

/**
 * Returns the tracking map for the offer count. In the use case where there
 * is an expired offer from `finalOffers` it will return empty (no tracking).
 * @param {Array} finalOffers - offers that are displayed (or used) on the page.
 * @param {Object} modsRecord
 * @param {Array} solicitedOffers (optional) - ALL offers that are solicited
 * @returns {Object}
 */
export const getOfferCountTrackingMap = (finalOffers, modsRecord, solicitedOffers) => {
  const trackingMap = {};

  if (getNumberOfExpiredOffers(finalOffers) > 0) {
    // Return empty right away
    return trackingMap;
  }

  const kmxOffer = get(modsRecord, ['partners', BRANDS.KMX, 'offers', 0]);
  const allOffers = compact([kmxOffer, ...(solicitedOffers || getSolicitedOffers(modsRecord))]);

  trackingMap[TrackingConstant.TOTAL_OFFER_COUNT] = generateSubactionPageloadTracking(
    TrackingConstant.TOTAL_OFFER_COUNT,
    allOffers.length
  );
  trackingMap[TrackingConstant.ACCEPTED_OFFER_COUNT] = generateSubactionPageloadTracking(
    TrackingConstant.ACCEPTED_OFFER_COUNT,
    allOffers.filter(acceptedOfferFilter).length
  );
  trackingMap[TrackingConstant.DECLINED_OFFER_COUNT] = generateSubactionPageloadTracking(
    TrackingConstant.DECLINED_OFFER_COUNT,
    allOffers.filter(declinedOfferFilter).length
  );

  return trackingMap;
};

/**
 * Returns the tracking map based off the offers that are displayed.
 * @param {Array} offers
 * @returns {Object}
 */
export const getOfferDisplayCountTrackingMap = offers => {
  const trackingMap = {};

  const numberOfExpiredOffers = getNumberOfExpiredOffers(offers);

  trackingMap[TrackingConstant.ACTIVE_OFFER_DISPLAY_COUNT] = generateSubactionPageloadTracking(
    TrackingConstant.ACTIVE_OFFER_DISPLAY_COUNT,
    offers.length - numberOfExpiredOffers
  );
  trackingMap[TrackingConstant.EXPIRED_OFFER_DISPLAY_COUNT] = generateSubactionPageloadTracking(
    TrackingConstant.EXPIRED_OFFER_DISPLAY_COUNT,
    numberOfExpiredOffers
  );
  trackingMap[TrackingConstant.OFFER_DISPLAY_COUNT] = generateSubactionPageloadTracking(
    TrackingConstant.OFFER_DISPLAY_COUNT,
    offers.length
  );

  return trackingMap;
};

/**
 * @param {Array} solicitedPartners
 * @returns {Object} tracking event object for solicited partners
 */
export const getSolicitedPartnersTrackingMap = solicitedPartners => ({
  [TrackingConstant.PARTNERS_TO_SOLICIT]: generateSubactionPageloadTracking(
    TrackingConstant.PARTNERS_TO_SOLICIT,
    solicitedPartners.length ? map(solicitedPartners, 'partnerToSolicit').join(',') : null
  ),
});

export const getMultiOfferTrackingMap = isMultiOffer => ({
  [TrackingConstant.MULTI_OFFER]: generateSubactionPageloadTracking(TrackingConstant.MULTI_OFFER, isMultiOffer),
});

export const getOffersPendingTrackingMap = () => ({
  [TrackingConstant.OFFERS_PENDING]: generateSubactionPageloadTracking(TrackingConstant.OFFERS_PENDING, undefined),
});

/**
 * Returns the tracking map for "subaction_name: ab_tests".
 * @param {Object} "seller" data from MODS record.
 * @returns {Object} tracking event object for seller ab_test record data
 */
export const getSellerAbTestTrackingMap = seller => {
  const abTests = get(seller, ['abTestBucketing'], []);
  return {
    [TrackingConstant.MODS_AB_TEST_BUCKETING]: generateSubactionPageloadTracking(
      TrackingConstant.MODS_AB_TEST_BUCKETING,
      abTests.length ? abTests.join(',') : null
    ),
  };
};

export const getDeclineReasonTrackingMap = (offers, brand, partner) => {
  const brandOffer = getBrandOffer(offers, brand, partner);
  const status = brandOffer?.status;
  const declineReason = brandOffer?.declineReason;

  if (status === 'declined' && declineReason)
    return {
      [`${TrackingConstant.SUBACTION_DECLINE_REASON_VALUE}_${brand}`]: generateSubactionPageloadTracking(
        `${TrackingConstant.SUBACTION_DECLINE_REASON_VALUE}_${brand}`,
        declineReason
      ),
    };

  return {};
};

export const buildOfferBrandPositionKey = brand => `offer_brand_position_${brand}`;

/**
 * Returns the tracking map based off the offer brand.
 * @param {Array} finalOffers offers data that are filtered.
 * @returns {Object} Returns an object with the key as subaction_name and the value of the tracking data.
 */
export const getOfferBrandsTrackingMap = finalOffers => {
  const trackingMap = {};
  const hasExpiredOffers = getNumberOfExpiredOffers(finalOffers) > 0;

  finalOffers.forEach((offer, index) => {
    const { brand } = offer;
    const isEstimated = isEstimatedOffer(offer);
    const value = isEstimated ? `${brand}-estimate` : brand;

    if (isExpiredOffer(offer)) {
      trackingMap[`expired_offer_brand_${brand}`] = generateSubactionPageloadTracking(
        TrackingConstant.EXPIRED_OFFER_BRAND,
        value
      );
    } else {
      const key = hasExpiredOffers ? `offer_brand_${brand}` : buildOfferBrandPositionKey(brand); // Key needs to be unique
      const subactionName = hasExpiredOffers
        ? TrackingConstant.OFFER_BRAND
        : `${TrackingConstant.OFFER_BRAND}_${index + 1}`;
      trackingMap[key] = generateSubactionPageloadTracking(subactionName, value);
    }
  });

  return trackingMap;
};

/**
 * Returns tracking map with all the page load pixels specific to ICO offer.
 *
 * @param {Object} icoOffer ICO offer from MODS record, may be absent.
 * @param {Boolean} isDisplayed if ICO offer is displayed to the user.
 * @param {Number} kmxOfferValuation valuation of CarMax offer.
 * @param {Object} vehicle vehicle object from MODS record.
 * @returns {Object} Returns an object with the key as subaction_name and the value of the tracking data.
 */
export const getIcoOfferTrackingMap = (icoOffer, isDisplayed, kmxOfferValuation, vehicle) => {
  if (isEmpty(icoOffer)) {
    return {};
  }
  const { icodsId, store, valuation } = icoOffer;
  const dealerId = store?.rooftopId;
  const storeData = JSON.parse(store?.rawData ?? '{}');
  const priorityMake = storeData?.priorityMake;
  const makeSlug = vehicle?.makeSlug;

  const result = {
    [TrackingConstant.ICO_DIRECT_ICODS_ID]: generateSubactionPageloadTracking(
      TrackingConstant.ICO_DIRECT_ICODS_ID,
      icodsId
    ),
    [TrackingConstant.ICO_DIRECT_DEALER_ID]: generateSubactionPageloadTracking(
      TrackingConstant.ICO_DIRECT_DEALER_ID,
      dealerId
    ),
    [TrackingConstant.ICO_DIRECT_DEALER_NAME]: generateSubactionPageloadTracking(
      TrackingConstant.ICO_DIRECT_DEALER_NAME,
      storeData?.name
    ),
    [TrackingConstant.ICO_MATCH_DEALER_MAKE]: generateSubactionPageloadTracking(
      TrackingConstant.ICO_MATCH_DEALER_MAKE,
      [makeSlug, priorityMake].every(value => typeof value === 'string') ? makeSlug === priorityMake : null
    ),
  };

  if (valuation && kmxOfferValuation) {
    result[TrackingConstant.ICO_DIRECT_OFFER_DELTA] = generateSubactionPageloadTracking(
      TrackingConstant.ICO_DIRECT_OFFER_DELTA,
      valuation - kmxOfferValuation
    );
  }

  if (isDisplayed) {
    result[TrackingConstant.ICO_DIRECT_OFFER_CARD] = generateSubactionPageloadTracking(
      TrackingConstant.ICO_DIRECT_OFFER_CARD,
      `ICO_dealerid_${dealerId}`
    );
  }

  return result;
};

export const getModsSourceTrackingMap = source => ({
  [TrackingConstant.MODS_SOURCE]: generateSubactionPageloadTracking(TrackingConstant.MODS_SOURCE, source ?? null),
});

/**
 * The reasons why there are...
 *  1. two DEFAULT expired render types: "at least one expired" and "all expired" has a slightly different UI.
 *  2. two carmax only render types: "only-carmax" "only-carmax-expired" has a slightly different UI.
 *
 * See EMO-358 (DEFAULT offers) and EMO-300 (Carmax only offers) for full details.
 * @param {Boolean} offersArePending
 * @param {Array} offers
 * @returns {String} renderType
 */
export const getMorPageRenderType = (offersArePending, offers = []) => {
  const acceptedOffers = offers.filter(acceptedOfferFilter); // Exclude estimated offer.
  const numberOfExpiredOffers = getNumberOfExpiredOffers(acceptedOffers);
  const hasExpiredOffers = numberOfExpiredOffers > 0;

  switch (true) {
    case offersArePending: // If any offers are still pending
      return MULTI_OFFER_RESULTS.PENDING;
    case hasExpiredOffers: // If all or some offers are expired
      return numberOfExpiredOffers === acceptedOffers.length
        ? MULTI_OFFER_RESULTS.DEFAULT_ALL_EXPIRED
        : MULTI_OFFER_RESULTS.DEFAULT_HAS_EXPIRED;
    default:
      return MULTI_OFFER_RESULTS.DEFAULT;
  }
};

export const getOdpPageRenderType = (offer, partnersToSolicit) => {
  const expired = isExpiredOffer(offer);
  const estimated = isEstimatedOffer(offer);

  switch (true) {
    case isEmpty(partnersToSolicit):
      return OFFER_DETAILS.NO_PARTNERS;
    case isEmpty(offer):
      return OFFER_DETAILS.NO_OFFER;
    case !acceptedOfferFilter(offer) && !estimated:
      return OFFER_DETAILS.NO_ESTIMATE;
    case estimated && expired:
      return OFFER_DETAILS.ESTIMATED_EXPIRED;
    case estimated:
      return OFFER_DETAILS.ESTIMATED;
    case expired:
      return OFFER_DETAILS.EXPIRED;
    default:
      return OFFER_DETAILS.DEFAULT;
  }
};

const enhanceOfferObjects = ({ offers = [] }) =>
  offers.map((offer, index) => {
    const offerNumber = index + 1;
    return {
      ...offer,
      offerNumber,
      offerDisplayNumber: offerNumber,
    };
  });

/**
 * Returns multi offer results object for the page (MOR page).
 * If there is error then it returns object empty case object.
 * @param {Object} data - data to help with returning the multi offer results object.
 * @returns {Object}
 */
export const getMultiOfferResultsObject = ({
  offers,
  vehicle,
  pageloadTrackingBySubactionName,
  offersArePending,
  isError = false,
  createdAt,
  updatedAt,
  seller,
  source,
  partnersOffersFinalizedAt,
}) => {
  if (isError) {
    return {
      isError,
      offers: [],
      vehicle: {},
      renderType: MULTI_OFFER_RESULTS.DEFAULT,
    };
  }

  return {
    offers: enhanceOfferObjects({ offers }),
    vehicle,
    pageloadTrackingBySubactionName,
    renderType: getMorPageRenderType(offersArePending, offers),
    createdAt,
    updatedAt,
    seller,
    source,
    partnersOffersFinalizedAt,
  };
};

/**
 * Returns object for offer details page (ODP).
 * If there is error then it returns empty case object.
 * @param {Object} data - data to help with returning the offer details page object.
 * @returns {Object}
 */
export const getOfferDetailsPageObject = ({
  offer,
  vehicle,
  pageloadTrackingBySubactionName,
  isError = false,
  seller,
  isMultiOffer,
  partnersOffersFinalizedAt,
  partnersToSolicit,
}) => {
  if (isError) {
    return {
      offer: {},
      vehicle: {},
      renderType: OFFER_DETAILS.DEFAULT,
      isError,
    };
  }

  return {
    offer: offer && { ...offer },
    vehicle,
    pageloadTrackingBySubactionName,
    renderType: getOdpPageRenderType(offer, partnersToSolicit),
    seller,
    isMultiOffer,
    partnersOffersFinalizedAt,
  };
};

export const findDealerInvoicingAccount = franchises => {
  if (!franchises) {
    return undefined;
  }
  for (let i = 0; i < franchises.length; i += 1) {
    const franchise = franchises[i];
    const productAttributes = franchise?.productFeatures?.productAttributes;
    if (productAttributes) {
      for (let j = 0; j < productAttributes.length; j += 1) {
        const productAttribute = productAttributes[j];
        const invoicingAccount = productAttribute?.attributes?.invoicingAccount;
        if (invoicingAccount) {
          return invoicingAccount;
        }
      }
    }
  }
  return undefined;
};

export const prepareModsRecord = rawModsRecord => ({
  ...rawModsRecord,
  vehicle: {
    ...rawModsRecord.vehicle,
    // Raw mods records don't have 'vin' in 'vehicle', so fill it in, since the UI components expect it.
    vin: rawModsRecord.vin,
  },
});

/**
 * Returns offers data for Multi-Offer Results (MOR) page.
 *
 * @param {Object} modsRecord MODS record.
 * @returns {{offersArePending, displayedOffers, nonKmxOffers}}
 *  offersArePending - Boolean flag that indicates if offers are NOT finalized yet (i.e. are pending).<br>
 *  displayedOffers - Array of offer objects that are sorted & displayed to the user on the MOR page. May include CarMax offer.<br>
 *  nonKmxOffers - Array of offer objects for non-CarMax partners. May include offers with status other than 'accepted' (e.g. 'declined').
 */
export const processMultiOfferResultsOffers = modsRecord => {
  const { partnersOffersFinalizedAt, partnersToSolicit = [], partners = {} } = modsRecord ?? {};

  const offersArePending = !partnersOffersFinalizedAt;

  if (offersArePending) {
    return {
      offersArePending: true,
      displayedOffers: [],
      nonKmxOffers: [],
    };
  }

  const solicitedOffers = partnersToSolicit.flatMap(partner => {
    const partnerObject = partners[partner] ?? {};
    const offers = partnerObject.offers ?? [];
    return offers.map(offer => constructOfferObject(offer, partner, partnerObject));
  });

  const displayedOffers = solicitedOffers
    .filter(({ isDisplayed }) => isDisplayed)
    .sort(sortOffersByValuationAndExpiredStatus);

  const nonKmxOffers = solicitedOffers.filter(({ brand }) => brand !== BRANDS.KMX);

  return {
    offersArePending,
    displayedOffers,
    nonKmxOffers,
  };
};

export const shouldRenderEmo3937 = offersResults => {
  // EMO-3937 does not render/apply if there are ANY expired offers.
  const acceptedOffers = offersResults?.offers?.filter(acceptedOfferFilter) ?? [];
  const isAppraisalOfferSource = getIsAppraisalsOfferSource(offersResults);
  const numberOfExpiredOffers = getNumberOfExpiredOffers(acceptedOffers); // Exclude estimated offer.
  return numberOfExpiredOffers === 0 && isAppraisalOfferSource;
};
